import { Inject } from '@odinjs/odin';
import moment from 'moment';

import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { FieldAPI, FormAPI, InputAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';

import { ReservedProduction } from '../../blood-management/blood-management-types';
import { PrescriptionType, solutionMapping } from '../../../services/timeline-service';
import { AdminModalsService } from '../../../services/admin-modals-service';
import MedicationService from '../../../services/medication-service';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { ApplicationRoute } from '../../../../../constants/routes';
import { Endpoint } from '../../../../../constants/endpoints';
import BaseController from '../../../../BaseController';
import { FragmentNames, ReservedBloodProductsRestartAliases } from '../../../../../codegen';

@Controller({ component: ReservedBloodProductsRestartAliases.RESERVED_BLOOD_PRODUCTS_RESTART_LAYOUT, fragment: FragmentNames.RESERVED_BLOOD_PRODUCTS_RESTART })
export default class ReservedBloodProductsController extends BaseController {

  @Inject(ReservedBloodProductsRestartAliases.NOTES_TEXT_AREA)
  private notesJustificationTextAreaApi!: TextAreaAPI;

  @Inject(ReservedBloodProductsRestartAliases.RESERVED_BLOOD_PRODUCTS_RESTART_FORM)
  private restartFormApi!: FormAPI

  @Inject(ReservedBloodProductsRestartAliases.SCHEDULE_DATE_INPUT)
  private scheduleDateInputApi!: InputAPI;

  @Inject(ReservedBloodProductsRestartAliases.TITLE_FIELD)
  private titleFieldApi!: FieldAPI;

  @Inject(ReservedBloodProductsRestartAliases.INFUSION_SPEED_FIELD)
  private infusionSpeedFieldApi!: FieldAPI;

  @Inject(ReservedBloodProductsRestartAliases.STEP_VOLUME_FIELD)
  private stepVolumeFieldApi!: FieldAPI;

  @OnMount(ReservedBloodProductsRestartAliases.RESERVED_BLOOD_PRODUCTS_RESTART_LAYOUT)
  handleMountReservedBloodProductsRestartLayout(): void {
    const infusionSpeedDefault = 0;
    const reservedProduct = this.context.getBulkData() as unknown as ReservedProduction;
    this.context.set({
      reservedProduct: { ...reservedProduct },
      scheduleDate: reservedProduct.reservationDate
    });
    this.restartFormApi.setData({
      ...reservedProduct,
      infusionSpeed: infusionSpeedDefault,
      stepVolume: reservedProduct.volume,
    });
    this.setDataPanelTitle(reservedProduct);
  }

  setDataPanelTitle(reservedProduct: ReservedProduction): void {
    const scheduleDate = moment(reservedProduct.reservationDate).utc().format(ISO_FULL_DATE_FORMAT);
    this.scheduleDateInputApi.setValue(scheduleDate);
    this.scheduleDateInputApi.setReadOnly(true);
    this.titleFieldApi.setData({ materialDescription: reservedProduct.description });
  }

  @OnBeforeCancel(ReservedBloodProductsRestartAliases.RESERVED_BLOOD_PRODUCTS_RESTART_FORM)
  handleCancelForm(): void {
    applicationContext.popNavigationItem();
  }

  @OnMount(ReservedBloodProductsRestartAliases.UNITY_INPUT)
  handleMountUnityField(event: MountEvent): void {
    const unityInputApi = event.target as InputAPI;
    unityInputApi.setValue(MessageService.translate('txt.drops-per-min'))
  }

  @OnMount(ReservedBloodProductsRestartAliases.DATE_INPUT)
  handleMountInputDate(event: MountEvent): void {
    const inputApi = event.target as InputAPI;
    const currentTimeMoment = moment();
    this.context.set({ currentTime: currentTimeMoment.format(ISO_FULL_DATE_FORMAT) });
    inputApi.setValue(currentTimeMoment.utc().format(ISO_FULL_DATE_FORMAT));
  }

  @OnBeforeSubmit(ReservedBloodProductsRestartAliases.RESERVED_BLOOD_PRODUCTS_RESTART_FORM)
  handleBeforeSubmit(): void {
    this.restartFormApi.validate();
    const body = this.getRequestBody();
    this.generatePresciptionChangesInterrupt(body)
  }

  private getRequestBody(): Record<string, Value> {
    const reservedProduct = this.context.get('reservedProduct');
    const { currentTime } = this.context.getBulkData() as { currentTime: string };
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const changeTypeRestart = 3;
    const dropsPerMin = 'gtm'
    return {
      bagNumber: reservedProduct.bagNumber,
      changeDate: currentTime,
      changeType: changeTypeRestart,
      dateTime: null,
      dosageType: dropsPerMin,
      encounterNumber: encounterId as number,
      infusionVelocityQuantity: this.infusionSpeedFieldApi.getValue(),
      justificationDescription: this.notesJustificationTextAreaApi.getValue() as string || '',
      prescriptionNumber: reservedProduct.prescriptionNumber,
      reservationProductSequenceNumber: reservedProduct.id,
      reservationSequenceNumber: reservedProduct.reservationId,
      solutionSequenceNumber: reservedProduct.materialSequence ?? 0,
      solutionType: solutionMapping[PrescriptionType.BLOOD_MANAGEMENT],
      volumePhaseQuantity: this.stepVolumeFieldApi.getValue()
    }
  }

  private generatePresciptionChangesInterrupt(body: Record<string, Value>): void {
    HttpService.post(Endpoint.ADEP_GENERATE_CHANGE_SOLUCAO_PRESCR, {}, { body })
      .then(response => {
        const responseBody = response.body as { messageDescription: string };
        if (responseBody) {
          this.showResponseMessage(responseBody?.messageDescription);
          this.redirectToTimeline();
        }
      }).catch((error) => {
        if (error.body && error.body.errors) {
          error.body.errors.forEach((message: string) => MedicationService.showToast(MessageService.translate(message), ToastLevel.ERROR));
        }
      })
  }

  private showResponseMessage(message: string): void {
    if (message) {
      MedicationService.showToast(message, ToastLevel.ERROR);
      return
    }
    MedicationService.showToast(MessageService.translate('txt.item-restarted-with-success'), ToastLevel.SUCCESS);
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    AdminModalsService.redirectToTimeline(FragmentNames.RESERVED_BLOOD_PRODUCTS_RESTART);
    AdminModalsService.redirectToTimeline(FragmentNames.RESERVED_BLOOD_PRODUCTS_VIEWER);
  }
}